import type { ResultOf } from "@graphql-typed-document-node/core";
import type { FormEvent, JSX } from "react";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useQuery } from "urql";
import Loader from "../../shared/components/Loader";
import ShowError from "../../shared/components/ShowError";
import { loadJSData } from "../../shared/jsData";
import t from "../../shared/translations";
import type { Language, TaskThemeType } from "../../shared/types";
import { graphql } from "../gql";
import Modal from "../Modal";
import type { IState } from "../state";
import type { IBuilderParams } from "./BuilderRoutes";
import Filter from "./Filter";
import ListHeading from "./ListHeading";
import Pagination from "./Pagination";
import SourceTask from "./SourceTask";
import TaskModal from "./TaskModal";

export type ISortBy = "name" | "miles" | "theme";
export type ISortDir = "increasing" | "decreasing";
export type ITaskType = "group-task" | "media" | "people-event" | "lecture" | "all";

const query = graphql(`
    query BuilderTaskPageQuery($locale: Language!, $page: Int!, $sortBy: String!, $sortDir: String!, $themes: [String!]!, $taskType: String!) {
        builderTaskPage(locale: $locale, page: $page, sortBy: $sortBy, sortDir: $sortDir, themes: $themes, taskType: $taskType) {
            countTotal
            builderTasks {
                id
                icon
                miles
                name
                price
                length
                locale
                description
                shortDescription
                learningCoachFeedback
                isGroupTask
                isMedia
                isPeopleEvent
                isLecture
                themes { id theme value }
            }
        }
    }
`);

export type IBuilderTask = ResultOf<typeof query>["builderTaskPage"]["builderTasks"][number];

const H3 = styled.h2`
    margin-top: 2rem;
`;

function Tasks(): JSX.Element {
    const { lang } = useParams<IBuilderParams>();
    const [page, setPage] = useState(1);
    const [[sortBy, sortDir], setSort] = useState<[ISortBy, ISortDir]>(["name", "increasing"]);
    const [themes, setThemes] = useState<TaskThemeType[]>(loadJSData().task_data.task_themes);
    const [taskType, setType] = useState<ITaskType>("all");
    const modalTask = useSelector((state: IState) => state.builder.modalTask);
    const [result] = useQuery({
        query,
        variables: {
            locale: lang as Language,
            page,
            sortBy,
            themes,
            sortDir,
            taskType,
        },
    });

    const onThemeChange = useCallback((event: FormEvent<HTMLInputElement>) => {
        const { checked, name } = event.currentTarget;
        setThemes((themes) => {
            if (checked) {
                return [...themes, name] as TaskThemeType[];
            }
            return themes.filter((thm) => thm !== name);
        });
    }, []);

    const onTypeChange = useCallback((event: FormEvent<HTMLInputElement>) => {
        setType(event.currentTarget.value as ITaskType);
    }, []);

    const toggleAllThemes = useCallback((on: boolean) => {
        if (on) {
            setThemes(loadJSData().task_data.task_themes);
        } else {
            setThemes([]);
        }
    }, []);

    let main = null;
    if (result.fetching) {
        main = <Loader />;
    } else if (result.error) {
        main = <ShowError error={t("public.error.error")} />;
    } else {
        const { builderTasks, countTotal } = result.data.builderTaskPage;
        const pageCount = Math.ceil(countTotal / 50);
        main = (
            <>
                <Pagination setPage={setPage} page={page} maxPage={pageCount} />
                <ListHeading showDetails={true} />
                <div>
                    {builderTasks.map((task) => (
                        <SourceTask task={task} key={task.id} />
                    ))}
                </div>
                <Pagination setPage={setPage} page={page} maxPage={pageCount} />
                <Modal modal="Builder/TaskDetails">
                    <TaskModal task={modalTask} onPath={false} />
                </Modal>
            </>
        );
    }
    return (
        <>
            <H3>{t("public.builder.heading-task-library")}</H3>
            <Filter
                themes={themes}
                taskType={taskType}
                onThemeChange={onThemeChange}
                onTypeChange={onTypeChange}
                setSort={setSort}
                toggleAllThemes={toggleAllThemes}
            />
            {main}
        </>
    );
}

export default Tasks;
